import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SaveIcon from "@mui/icons-material/Save";
import HomeIcon from "@mui/icons-material/Home";
import ClassIcon from "@mui/icons-material/Class";
import SchoolIcon from "@mui/icons-material/School";
import SideBar1 from "../chats/sidebar1";
import HomeHeader1 from "../../components/HomeHeader1";
import "../Home/homestyle.css";
import "./settingsProfile.css";
import { toast } from "react-toastify";
import CirculerLoader from "../../components/Loader";
import { decodeToken } from "../../utils/commonFunctions";
import { generateGradeOptions } from "../../utils/QuestionsList";

const SettingsProfile = () => {
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_BASE_URL;
  const [deepHope, setDeepHope] = useState("");
  const [storylines, setStorylines] = useState("");
  const [className, setClassName] = useState("");
  const [subject, setSubject] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const userTokenData = decodeToken();
  const masterTypeid = Number(userTokenData?.masterTypeid);
  const isTft = masterTypeid === 1;
  const isScsbc = masterTypeid === 2;
  const gradeOptions = useMemo(() => generateGradeOptions(), []);
  const selectedGrade = useMemo(
    () => gradeOptions.find((grade) => grade.label === className || grade.value === className),
    [gradeOptions, className]
  );
  const subjectOptions = selectedGrade?.children || [];

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setLoading(false);
      return;
    }
    axios
      .get(`${baseURL}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.code === 200 && res.data?.data) {
          const { deephope, storyline, class: classValue, subject: subjectValue } = res.data.data;
          setDeepHope(deephope ?? "");
          setStorylines(storyline ?? "");
          setClassName(classValue ?? "");
          setSubject(subjectValue ?? "");
          localStorage.setItem(
            "profile_defaults",
            JSON.stringify({
              className: classValue ?? "",
              subject: subjectValue ?? "",
            })
          );
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message ?? err.response?.data?.detail ?? "Failed to load profile.";
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  }, [baseURL]);

  const handleSave = (e) => {
    e.preventDefault();
    const token = localStorage.getItem("access_token");
    if (!token) {
      toast.error("Please sign in to save.");
      return;
    }
    setSaving(true);
    axios
      .put(
        `${baseURL}/profile`,
        isTft
          ? {
              deephope: deepHope.trim() || null,
              storyline: storylines.trim() || null,
            }
          : isScsbc
          ? {
              class: className.trim() || null,
              subject: subject.trim() || null,
            }
          : {
              class: className.trim() || null,
              subject: subject.trim() || null,
            },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((res) => {
        if (res.data?.code === 200) {
          localStorage.setItem(
            "profile_defaults",
            JSON.stringify({
              className: className.trim() || "",
              subject: subject.trim() || "",
            })
          );
          toast.success(res.data.message ?? "Profile saved.");
        } else {
          toast.error(res.data?.message ?? "Failed to save.");
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message ?? err.response?.data?.detail ?? "Failed to save profile.";
        toast.error(msg);
      })
      .finally(() => setSaving(false));
  };

  const handleClassChange = (e) => {
    const selectedClass = e.target.value;
    setClassName(selectedClass);

    const matchingGrade = gradeOptions.find(
      (grade) => grade.label === selectedClass || grade.value === selectedClass
    );
    const availableSubjectLabels = (matchingGrade?.children || []).map((item) => item.label);
    if (subject && !availableSubjectLabels.includes(subject)) {
      setSubject("");
    }
  };

  return (
    <div className="sidebar-content-other">
      <SideBar1 />
      <div className="home-page-content-inner">
        <HomeHeader1 />
        <div className="settings-profile-page settings-profile-inn">
         <div className="welcome-main-teaching">
          <div className="settings-profile-hero">
            {/* <div className="settings-profile-hero-icon">
              <PersonOutlineIcon sx={{ fontSize: 36 }} />
            </div> */}
            {/* {userEmail && (
              <div className="settings-profile-email">
                <EmailIcon className="settings-profile-email-icon" />
                <span>{userEmail}</span>
              </div>
            )} */}
            {!isScsbc && (
              <p className="settings-profile-subtitle">
                Share your aspirations and personal journey to help us tailor your experience.
              </p>
            )}
          </div>
          <div className="settings-profile-card setting-profile-inn">
            {loading ? (
              <div className="settings-profile-loading">
                <CirculerLoader />
              </div>
            ) : (
              <form onSubmit={handleSave} className="settings-profile-form settings-profile-inn">
                {isTft ? (
                  <>
                    <div className="settings-field-card">
                      <div className="settings-field-header">
                        <ScienceIcon className="settings-field-icon" />
                        <label htmlFor="deep-hope">Deep Hope</label>
                      </div>
                      <textarea
                        id="deep-hope"
                        className="settings-textarea setting-text-inn"
                        value={deepHope}
                        onChange={(e) => setDeepHope(e.target.value)}
                        placeholder="Describe your deep hope or aspiration for your students."
                        rows={4}
                      />
                    </div>
                    <div className="settings-field-card">
                      <div className="settings-field-header">
                        <MenuBookIcon className="settings-field-icon" />
                        <label htmlFor="storylines">Story</label>
                      </div>
                      <textarea
                        id="storylines"
                        className="settings-textarea"
                        value={storylines}
                        onChange={(e) => setStorylines(e.target.value)}
                        placeholder="Describe the storyline, context, or narrative you would like to develop."
                        rows={4}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="settings-field-card">
                      <div className="settings-field-header">
                        <ClassIcon className="settings-field-icon" />
                        <label htmlFor="class-name">Grade</label>
                      </div>
                      <select
                        id="class-name"
                        className="settings-textarea"
                        value={className}
                        onChange={handleClassChange}
                      >
                        <option value="">Select grade</option>
                        {className && !gradeOptions.some((option) => option.label === className) && (
                          <option value={className}>{className}</option>
                        )}
                        {gradeOptions.map((option) => (
                          <option key={option.value} value={option.label}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="settings-field-card">
                      <div className="settings-field-header">
                        <SchoolIcon className="settings-field-icon" />
                        <label htmlFor="subject">Subject</label>
                      </div>
                      <select
                        id="subject"
                        className="settings-textarea"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        disabled={!className}
                      >
                        <option value="">Select subject</option>
                        {subject && !subjectOptions.some((option) => option.label === subject) && (
                          <option value={subject}>{subject}</option>
                        )}
                        {subjectOptions.map((option) => (
                          <option key={option.value} value={option.label}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                <div className="settings-profile-actions">
                  
                  <Button
                    type="submit"
                    variant="contained"
                    className="settings-save-btn"
                    disabled={saving}
                    startIcon={saving ? null : <SaveIcon />}
                    sx={{
                      textTransform: "none",
                      fontFamily: "'Figtree', sans-serif",
                      fontWeight: 600,
                      fontSize: "15px",
                      padding: "12px 28px",
                      borderRadius: "10px",
                      backgroundColor: "#2C68A1",
                      boxShadow: "0 4px 14px rgba(44, 104, 161, 0.35)",
                      "&:hover": {
                        backgroundColor: "#235a8f",
                        boxShadow: "0 6px 20px rgba(44, 104, 161, 0.4)",
                      },
                    }}
                  >
                    {saving ? <CirculerLoader /> : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    className="settings-home-btn"
                    onClick={() => navigate("/")}
                    startIcon={<HomeIcon />}
                    sx={{
                      textTransform: "none",
                      fontFamily: "'Figtree', sans-serif",
                      fontWeight: 600,
                      fontSize: "15px",
                      padding: "12px 28px",
                      borderRadius: "10px",
                      backgroundColor: "#fff",
                      borderColor: "#2C68A1",
                      color: "#2C68A1",
                      "&:hover": {
                        borderColor: "#235a8f",
                        backgroundColor: "rgba(44, 104, 161, 0.06)",
                        color: "#235a8f",
                      },
                    }}
                  >
                    Home
                  </Button>
                </div>
              </form>
            )}
          </div>
         </div>  
        </div>
      </div>
    </div>
  );
};

export default SettingsProfile;
